import { useEffect, useState } from "react";
import Button, { ButtonTypes } from "../../../common/components/button/button";
import WebLayout from "../../../common/components/layouts/webLayout/webLayout";
import Table from "../../../common/components/table/table";
import CreateUserPopup from "../components/popups/createUserPopup/createUserPopup";
import { userTableColumns } from "../constants/userTableColumns";
import { ReactComponent as EditIcon } from "../../../assets/editIcon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/trashIcon.svg";
import "./userManagementPage.scss";
import {
  useErrorHandler,
  useLoadHandler,
} from "../../../common/customHooks/useLoadHandler";
import { UserApi } from "../../../common/services/api/userApi";
import Toggle from "../../../common/components/toggle/toggle";
import { UserListModel } from "../models/userListModel";
import ConfirmationPopup from "../../../common/components/confirmationPopup/confirmationPopup";
import { NewUserModel } from "../models/newUserModel";
import EditUserPopup from "../components/popups/editUserPopup/editUserPopup";

export default function UserManagementPage() {
  const [showCreateUserPopup, setShowCreateUserPopup] = useState(false);
  const [showEditUserPopup, setShowEditUserPopup] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserListModel>();
  const [userData, setUserData] = useState<NewUserModel>();
  const [rows, setRows] = useState<
    {
      key: string;
      value: any;
    }[][]
  >([]);

  const { loadingStatus, callFetchAsync } = useLoadHandler(async () => {
    const result = await getCampaignRows();
    return setRows(result);
  });

  const { callFetchAsync: toggleCallFetchAsync } = useErrorHandler(
    async (id: string) => {
      await UserApi().toggleCampaignActive(id);
      return callFetchAsync();
    }
  );

  const {
    loadingStatus: deleteLoadingStatus,
    callFetchAsync: deleteCallFetchAsync,
  } = useLoadHandler(async (id: string) => {
    await UserApi().deleteCampaign(id);
    return callFetchAsync();
  });

  const {
    loadingStatus: newUserLoadingStatus,
    callFetchAsync: newUserCallFetchAsync,
  } = useLoadHandler(async (user: NewUserModel) => {
    await UserApi().addNewUser(user);
    return callFetchAsync();
  });

  const {
    loadingStatus: editUserLoadingStatus,
    callFetchAsync: editUserCallFetchAsync,
  } = useLoadHandler(async ([user, userId]: [NewUserModel, string]) => {
    await UserApi().editUser(user, userId);
    return callFetchAsync();
  });

  const {
    loadingStatus: userDataLoadingStatus,
    callFetchAsync: userDataCallFetchAsync,
  } = useLoadHandler(async (userId: string) => {
    let result = await UserApi().userData(userId);
    return setUserData(result);
  });

  useEffect(() => {
    callFetchAsync();
  }, []);

  async function getCampaignRows() {
    let result = ((await UserApi().users()) ?? []) as UserListModel[];
    return result.map((e) => [
      { key: "username", value: e.username },
      { key: "fullName", value: e.fullName },
      { key: "email", value: e.email },
      { key: "role", value: e.role },
      {
        key: "active",
        value: e.id != userData?.userId && (
          <Toggle
            isOn={e.isActive}
            onChange={() => toggleCallFetchAsync(e.id)}
          />
        ),
      },
      {
        key: "actions",
        value: (
          <div className="user-actions-container">
            <EditIcon
              style={{ cursor: "pointer" }}
              height={20}
              width={20}
              onClick={async () => {
                setSelectedUser(e);
                userDataCallFetchAsync(e.id);
                setShowEditUserPopup(true);
              }}
            />
            {e.id != userData?.userId && (
              <TrashIcon
                style={{ cursor: "pointer" }}
                height={20}
                width={20}
                stroke={"red"}
                onClick={() => {
                  setSelectedUser(e);
                  setShowConfirmation(true);
                }}
              />
            )}
          </div>
        ),
      },
    ]);
  }

  return (
    <>
      <WebLayout pageIndex={1}>
        <div className="head">
          <div>
            <h3 style={{ margin: 0 }}>Techsauce</h3>
            <p style={{ fontSize: "12px" }}>User Management System</p>
          </div>
          <Button
            className="add-user-button"
            type={ButtonTypes.PRIMARY}
            text="+ Add New User"
            onClick={() => setShowCreateUserPopup(true)}
          />
        </div>
        <Table
          columns={userTableColumns}
          loadingStatus={loadingStatus}
          rows={rows}
        />
      </WebLayout>
      {showCreateUserPopup && (
        <CreateUserPopup
          loadingStatus={newUserLoadingStatus}
          onClickClose={() => setShowCreateUserPopup(false)}
          onSubmit={async (e) => {
            await newUserCallFetchAsync(e);
            setShowCreateUserPopup(false);
          }}
        />
      )}
      {showEditUserPopup && (
        <EditUserPopup
          key={`${userData}`}
          userData={userData}
          dataLoadingStatus={userDataLoadingStatus}
          submitLoadingStatus={editUserLoadingStatus}
          onClickClose={() => setShowEditUserPopup(false)}
          onSubmit={async (e) => {
            await editUserCallFetchAsync([e, selectedUser?.id]);
            setShowEditUserPopup(false);
          }}
        />
      )}
      {showConfirmation && (
        <ConfirmationPopup
          description={`Are you sure you want to delete "${selectedUser?.username}"`}
          loadingStatus={deleteLoadingStatus}
          onConfirm={async () => {
            await deleteCallFetchAsync(selectedUser?.id);
            setShowConfirmation(false);
          }}
          onCancel={() => setShowConfirmation(false)}
        />
      )}
    </>
  );
}
